/* @flow */
import { parse as parseUrl } from 'url';
import initializeStore from './initializeStore';
import {
  loadLocaleForEdition,
  setGeolocation,
  setShouldShowGeolocationBanner
} from '../actions/i18n';
import { deobfuscate } from '../initializers/obfuscate';
import getLocaleDataForEdition from '../utils/getLocaleDataForEdition';
import getDevPageConfig from '../utils/getDevPageConfig';
import { pageConfigIsValid } from '../utils/pageConfig';
import { createCookiesObjectOnDev } from '../utils/cookie';
import { shouldShowGeolocationBanner } from '../utils/geolocation';
import type { LocaleData } from '../actions/i18n';
import { onProduction } from '../utils/env';
import { WORDPRESS_API_HOST } from '../constants';

type Breakpoint =
    'phone'
  | 'tablet'
  | 'desktop'
;

type Store = {
  dispatch: (action: Promise<Object>|Object) => any,
  getState: () => Object
};

type PageConfigFromHeaders = {
  site: string,
  edition_id: number,
  type: string,
  type_id: string,
  hostname: string,
  isPreview: bool
};

type Query = {
  site?: string,
  edition_id?: string,
  type?: string,
  type_id?: string,
  hostname?: string,
  isPreview?: bool,
  geo_override?: string,
  template?: string
};

type ExpressRequestObject = {
  get: (param: string) => ?string,
  pageConfig: PageConfigFromHeaders,
  query: Query,
  path: string,
  cookies: Object
};

const getEditionId = (
  query: Query,
  pageConfigFromNginx: PageConfigFromHeaders
): number =>
  pageConfigFromNginx.isPreview
    ? parseInt(query.edition_id)
    : pageConfigFromNginx.edition_id;

const loadLocaleData = (store: Store, editionId: number): Promise<Store> =>
  getLocaleDataForEdition(editionId)
    .then((localeData: LocaleData): Store => {
      store.dispatch(loadLocaleForEdition(localeData));
      return store;
    });

const createClientSideDevStore =
  (window: window): Promise<Store> => { // eslint-disable-line no-use-before-define
    const { pathname, search } = window.location;
    const { query = {} } = parseUrl(search, true);
    const pageConfig = getDevPageConfig(pathname, query);
    if (!pageConfigIsValid(pageConfig)) {
      throw new Error(`
        PageConfig for ${window.location.pathname} empty!
        Check js/utils/getDevPageConfig.js.
      `);
    }

    const editionId = getEditionId(query, pageConfig);
    const cookies = createCookiesObjectOnDev();
    const store = initializeStore({
      isPreviewPage: pageConfig.isPreview,
      cookies,
      wordpressApiHost: WORDPRESS_API_HOST
    });

    const { geo_override } = query;
    if (geo_override) {
      store.dispatch(setGeolocation(geo_override));
    }
    store.dispatch(setShouldShowGeolocationBanner(
      shouldShowGeolocationBanner(geo_override, editionId)
    ));

    return loadLocaleData(store, editionId);
  };

export const createClientSideStore =
  (window: Object, document: Object): Promise<Store>|Error => {
    const preloadedStateElement = document.getElementById('r29-initial-state');
    if (!preloadedStateElement) {
      if (onProduction()) {
        throw new Error('Error 1: Unsupported.');
      }
      return createClientSideDevStore(window);
    }

    const preloadedState = JSON.parse(
      deobfuscate(preloadedStateElement.innerHTML)
    );
    return Promise.resolve(initializeStore(preloadedState));
  };

export const createServerSideStore = (
  req: ExpressRequestObject,
  guessedBreakpoint: Breakpoint
): Promise<Store> => {
  const store = initializeStore({
    ui: { guessedBreakpoint },
    isPreviewPage: req.pageConfig.isPreview,
    cookies: req.cookies,
    wordpressApiHost: WORDPRESS_API_HOST
  });

  const editionId = getEditionId(req.query, req.pageConfig);
  const geolocation = req.query.geo_override || req.get('X-Geolocation');
  if (geolocation) {
    store.dispatch(setGeolocation(geolocation));
  }
  store.dispatch(setShouldShowGeolocationBanner(
    shouldShowGeolocationBanner(geolocation, editionId)
  ));

  return loadLocaleData(store, editionId);
};
